import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useForm, UseFormReturn } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { useToast } from "@/hooks/use-toast";
import { useSmartBack } from "@/lib/navigation-utils";

import { type OwnerInput } from "@/lib/validations/owner";
import { ownerWizardSchema } from "@/lib/validations/ownerWizard";
import { type PetInput } from "@/lib/validations/pet";
import { petWizardSchema } from "@/lib/validations/petWizard";
import { useCreateOwner, useOwner } from "@/hooks/useOwners";
import { useOwnerSearch } from "@/hooks/useOwnerSearch";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useCreatePet, useNextPetRecordNumber } from "@/hooks/usePets";
import { useUploadPetAttachment, useUploadPetPhoto } from "@/hooks/useUpload";
import {
  useDocumentTypes,
  useGenders,
  useLeadSources,
  useStates,
  type City,
  type DocumentType,
} from "@/hooks/useLookups";

import { PetFormItem } from "@/components/owners/PetFormItem";
import { OwnerFormSection } from "@/components/owners/OwnerFormSection";
import { Button } from "@akuna/ui";
import { SearchField } from "@akuna/ui";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@akuna/ui";
import { Input } from "@akuna/ui";
import { Textarea } from "@/components/ui/textarea";
import { Autocomplete } from "@/components/ui/autocomplete";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { rollbackOwnerOrQueue } from "@/lib/patients/ownerRollbackQueue";
import {
  convertDisplayToISO,
  formatISOToDisplay,
  getDateErrorMessage,
  maskDateInput,
} from "@/lib/date-utils";
import { ArrowLeft, CalendarIcon, CheckCircle2, EyeOff, Plus, Trash2, User, Search, Mail } from "lucide-react";
import { CustomIcons } from "@/components/icons/CustomIcons";
import {
  applyDocumentMask,
  removeDocumentMask,
  validateDocument,
  getDocumentPlaceholder,
  getDocumentMaxLength,
} from "@/lib/utils/document-masks";
import { CepLookupError, lookupCep, mapCepErrorToMessage } from "@/lib/cep";
import { startCancelableSmoothScroll } from "@/lib/cancelableSmoothScroll";

type Step = 1 | 2;

const ownerEmptyValues: OwnerInput = {
  name: "",
  gender_id: "",
  document_type_id: "",
  document: "",
  birth_date: "",
  profession: "",
  marketing_consent: true,
  lead_source_id: "",
  phones: [{ phone_number: "", is_whatsapp: true, is_primary: true }],
  // Responsável: apenas 1 email
  emails: [{ email: "", is_primary: true }],
  // Sempre exibir pelo menos 1 endereço em branco
  addresses: [
    {
      zip_code: "",
      street: "",
      number: "",
      complement: "",
      neighborhood: "",
      city_id: "",
      is_primary: true,
    },
  ],
};

const maskPhoneInput = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 11);

  if (digits.length <= 10) {
    const part1 = digits.slice(0, 2);
    const part2 = digits.slice(2, 6);
    const part3 = digits.slice(6, 10);

    return [part1 && `(${part1}`, part1 && part1.length === 2 ? ") " : "", part2, part3 && `-${part3}`]
      .join("")
      .trim();
  }

  const part1 = digits.slice(0, 2);
  const part2 = digits.slice(2, 7);
  const part3 = digits.slice(7, 11);

  return [part1 && `(${part1}`, part1 && part1.length === 2 ? ") " : "", part2, part3 && `-${part3}`]
    .join("")
    .trim();
};

const maskCepInput = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 5) {
    return digits;
  }
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
};

/**
 * Formata documento (CPF/CNPJ) para exibição
 */
const formatDocument = (document: string | null): string => {
  if (!document) return "";

  const digits = document.replace(/\D/g, "");

  // Se for CPF (11 dígitos)
  if (digits.length === 11) {
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
  }

  // Se for CNPJ (14 dígitos)
  if (digits.length === 14) {
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
  }

  return document;
};

export default function NewPatientWizard() {
  const cancelScrollRef = useRef<(() => void) | null>(null);
  const { t } = useTranslation("patients");
  const navigate = useNavigate();
  const smartBack = useSmartBack();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const ownerIdFromUrl = searchParams.get("owner_id");
  const mode = searchParams.get("mode");
  const [step, setStep] = useState<Step>(ownerIdFromUrl ? 2 : 1);
  const [step1Mode, setStep1Mode] = useState<"search" | "create">(
    mode === "create" ? "create" : "search"
  );

  // Estados para busca de owner
  const [ownerSearchInput, setOwnerSearchInput] = useState("");
  const debouncedOwnerSearch = useDebouncedValue(ownerSearchInput.trim(), 400);
  const { data: searchedOwners = [], isLoading: isLoadingOwnerSearch } = useOwnerSearch(debouncedOwnerSearch);
  const [selectedOwnerIdFromSearch, setSelectedOwnerIdFromSearch] = useState<string | null>(null);

  // Owner fields state (ported from CreateOwner to preserve behavior)
  const [birthDateInput, setBirthDateInput] = useState("");
  const [isBirthDateCalendarOpen, setIsBirthDateCalendarOpen] = useState(false);
  const [documentInput, setDocumentInput] = useState("");
  const [addressStates, setAddressStates] = useState<(string | null)[]>([]);
  const [addressCities, setAddressCities] = useState<Record<number, City[]>>({});
  const [addressCepLoading, setAddressCepLoading] = useState<Record<number, boolean>>({});
  const [cityNameCache, setCityNameCache] = useState<Record<string, string>>({});
  const [cityDisplayValues, setCityDisplayValues] = useState<Record<number, string>>({});

  // Pets state (mirrors CreateOwner multi-pet handling)
  const [petForms, setPetForms] = useState<Record<string, UseFormReturn<PetInput>>>({});
  const [petIds, setPetIds] = useState<string[]>(() => {
    const id = `pet-${Date.now()}-${Math.random().toString(36).substring(7)}`;
    return [id];
  });
  const [lastCreatedPetId, setLastCreatedPetId] = useState<string | null>(null);
  const [petDefaultRecordNumbers, setPetDefaultRecordNumbers] = useState<Record<string, string | null>>({});
  const recordBaseRef = useRef<number | null>(null);
  const nextOffsetRef = useRef<number>(0);

  // Exit confirmation dialog state
  const [isExitDialogOpen, setIsExitDialogOpen] = useState(false);

  const { toast } = useToast();
  const createOwner = useCreateOwner();
  const createPet = useCreatePet();
  const {
    data: nextRecordNumber,
    isLoading: isLoadingNextRecordNumber,
    isError: isNextRecordNumberError,
  } = useNextPetRecordNumber();
  const uploadPhoto = useUploadPetPhoto();
  const uploadAttachment = useUploadPetAttachment();

  const { data: genders } = useGenders();
  const { data: documentTypes } = useDocumentTypes();
  const { data: leadSources } = useLeadSources();
  const { data: states } = useStates();

  // Buscar owner se owner_id estiver na URL
  const { data: existingOwner, isLoading: isLoadingOwner } = useOwner(ownerIdFromUrl || "");
  // Buscar owner selecionado da busca
  const { data: selectedOwnerFromSearch } = useOwner(selectedOwnerIdFromSearch || "");
  const scrollToTopOnError = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const form = useForm<OwnerInput>({
    // Wizard: only `name` is required for the owner. (Keep stricter validation in Create/Edit Owner.)
    resolver: zodResolver(ownerWizardSchema),
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    defaultValues: ownerEmptyValues,
  });

  const resetOwnerForCreateMode = useCallback(() => {
    form.reset(ownerEmptyValues);
    form.clearErrors();

    setBirthDateInput("");
    setIsBirthDateCalendarOpen(false);
    setDocumentInput("");

    setAddressStates([]);
    setAddressCities({});
    setAddressCepLoading({});
    setCityNameCache({});
    setCityDisplayValues({});

    setSelectedOwnerIdFromSearch(null);
  }, [form]);

  const enterCreateMode = useCallback(() => {
    resetOwnerForCreateMode();
    setStep1Mode("create");
  }, [resetOwnerForCreateMode]);

  const addPetForm = () => {
    const id = `pet-${Date.now()}-${Math.random().toString(36).substring(7)}`;
    setPetIds((prev) => [...prev, id]);
    setLastCreatedPetId(id);
    setPetDefaultRecordNumbers((prev) => {
      if (recordBaseRef.current === null) {
        return { ...prev, [id]: null };
      }
      const offset = nextOffsetRef.current;
      nextOffsetRef.current += 1;
      return { ...prev, [id]: String(recordBaseRef.current + offset) };
    });
  };

  const removePetForm = (id: string) => {
    setPetIds((prev) => prev.filter((petId) => petId !== id));
    setPetForms((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setPetDefaultRecordNumbers((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const handleFormReady = useCallback((id: string, petForm: UseFormReturn<PetInput>) => {
    setPetForms((prev) => ({ ...prev, [id]: petForm }));
    // Preencher owner_id se estiver disponível
    if (ownerIdFromUrl) {
      petForm.setValue("owner_id", ownerIdFromUrl, { shouldDirty: false });
    }
  }, [ownerIdFromUrl]);

  const isNextRecordNumberInvalid = useMemo(() => {
    if (nextRecordNumber === null || nextRecordNumber === undefined) return false;
    return !Number.isFinite(Number(nextRecordNumber));
  }, [nextRecordNumber]);

  // Ensure we always have an entry for each petId (so we can decide UI state per pet row).
  useEffect(() => {
    setPetDefaultRecordNumbers((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const id of petIds) {
        if (!(id in next)) {
          next[id] = null;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [petIds]);

  // When the backend suggestion arrives, assign stable sequential defaults per petId (only for ids not assigned yet).
  useEffect(() => {
    if (isNextRecordNumberError) return;
    if (!nextRecordNumber) return;
    const parsedBase = Number(nextRecordNumber);
    if (!Number.isFinite(parsedBase)) return;

    if (recordBaseRef.current === null) {
      recordBaseRef.current = parsedBase;
    }

    setPetDefaultRecordNumbers((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const id of petIds) {
        if (next[id] === null || next[id] === undefined) {
          if (recordBaseRef.current === null) break;
          const offset = nextOffsetRef.current;
          nextOffsetRef.current += 1;
          next[id] = String(recordBaseRef.current + offset);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [isNextRecordNumberError, nextRecordNumber, petIds]);

  // Helper para obter email primário de um owner
  const primaryEmail = (owner: typeof searchedOwners[0]) => {
    if (!owner.emails || owner.emails.length === 0) return null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return owner.emails.find((e: any) => e.is_primary) || owner.emails[0];
  };

  // Handler para selecionar owner da busca
  const handleSelectOwnerFromSearch = (ownerId: string) => {
    const selectedOwner = searchedOwners.find((o) => o.id === ownerId);
    if (!selectedOwner) return;

    // Armazenar o ID do owner selecionado
    setSelectedOwnerIdFromSearch(ownerId);

    // Preencher form com dados do owner selecionado
    form.reset({
      name: selectedOwner.name || "",
      gender_id: selectedOwner.gender_id || "",
      document_type_id: selectedOwner.document_type_id || "",
      document: selectedOwner.document || "",
      birth_date: selectedOwner.birth_date || "",
      profession: selectedOwner.profession || "",
      marketing_consent: selectedOwner.marketing_consent ?? true,
      lead_source_id: selectedOwner.lead_source_id || "",
      phones: selectedOwner.phones && selectedOwner.phones.length > 0
        ? selectedOwner.phones.map((phone: { id?: string; phone_number?: string; is_whatsapp?: boolean; is_primary?: boolean }) => ({
          id: phone.id,
          phone_number: phone.phone_number || "",
          is_whatsapp: phone.is_whatsapp ?? false,
          is_primary: phone.is_primary ?? false,
        }))
        : [{ phone_number: "", is_whatsapp: true, is_primary: true }],
      emails: selectedOwner.emails && selectedOwner.emails.length > 0
        ? selectedOwner.emails.map((email: { id?: string; email?: string; is_primary?: boolean }) => ({
          id: email.id,
          email: email.email || "",
          is_primary: email.is_primary ?? false,
        }))
        : [{ email: "", is_primary: true }],
      addresses: selectedOwner.addresses && selectedOwner.addresses.length > 0
        ? selectedOwner.addresses.map((address: { id?: string; zip_code?: string; street?: string; number?: string; complement?: string; neighborhood?: string; city_id?: string; is_primary?: boolean; city?: { state_id?: string; name?: string } }, index: number) => {
          const city = address.city;
          if (city?.state_id) {
            setAddressStates((prev) => {
              const updated = [...prev];
              updated[index] = city.state_id;
              return updated;
            });
          }
          return {
            id: address.id,
            zip_code: address.zip_code || "",
            street: address.street || "",
            number: address.number || "",
            complement: address.complement || "",
            neighborhood: address.neighborhood || "",
            city_id: address.city_id || "",
            is_primary: address.is_primary ?? (index === 0),
          };
        })
        : [{
          zip_code: "",
          street: "",
          number: "",
          complement: "",
          neighborhood: "",
          city_id: "",
          is_primary: true,
        }],
    });

    if (selectedOwner.document) {
      setDocumentInput(selectedOwner.document);
    }

    if (selectedOwner.birth_date) {
      setBirthDateInput(formatISOToDisplay(selectedOwner.birth_date));
    }

    // Carregar cidades para os estados selecionados
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    selectedOwner.addresses?.forEach((address: any, index: number) => {
      const city = address.city;
      if (city?.state_id) {
        fetchCitiesForState(city.state_id, index, city.name).catch(() => {
          // Silencia erros
        });
      }
    });

    // Ir para passo 2
    setStep(2);
  };

  // === Owner helpers / derived values
  const documentTypeId = form.watch("document_type_id");
  const selectedDocumentType = useMemo(
    () => documentTypes?.find((type) => type.id === documentTypeId) as DocumentType | undefined,
    [documentTypes, documentTypeId],
  );
  const documentPlaceholder = getDocumentPlaceholder(selectedDocumentType);
  const documentMaxLength = getDocumentMaxLength(selectedDocumentType);

  const phones = form.watch("phones");
  const emails = form.watch("emails");
  const addresses = form.watch("addresses");

  const handleAddAddress = useCallback(() => {
    const currentAddresses = form.getValues("addresses") || [];
    form.setValue(
      "addresses",
      [
        ...currentAddresses,
        {
          zip_code: "",
          street: "",
          number: "",
          complement: "",
          neighborhood: "",
          city_id: "",
          is_primary: false,
        },
      ],
      { shouldDirty: true },
    );
    setAddressStates((prev) => [...prev, null]);
  }, [form]);

  const handleRemoveAddress = useCallback(
    (index: number) => {
      const currentAddresses = form.getValues("addresses") || [];
      const updatedAddresses = currentAddresses.filter((_, i) => i !== index);
      form.setValue("addresses", updatedAddresses, { shouldDirty: true });

      setAddressStates((prev) => prev.filter((_, stateIndex) => stateIndex !== index));

      setAddressCities((prev) => {
        const reordered: Record<number, City[]> = {};
        updatedAddresses.forEach((_, newIndex) => {
          const originalIndex = newIndex >= index ? newIndex + 1 : newIndex;
          if (Object.prototype.hasOwnProperty.call(prev, originalIndex)) {
            reordered[newIndex] = prev[originalIndex];
          }
        });
        return reordered;
      });

      setAddressCepLoading((prev) => {
        const reordered: Record<number, boolean> = {};
        updatedAddresses.forEach((_, newIndex) => {
          const originalIndex = newIndex >= index ? newIndex + 1 : newIndex;
          if (Object.prototype.hasOwnProperty.call(prev, originalIndex)) {
            reordered[newIndex] = prev[originalIndex];
          }
        });
        return reordered;
      });
    },
    [form],
  );

  // Keep document input masked and default doc type like CreateOwner does
  useEffect(() => {
    setDocumentInput((prev) => {
      const masked = applyDocumentMask(prev || form.getValues("document") || "", selectedDocumentType);
      if (masked !== prev) {
        form.setValue("document", masked, { shouldValidate: true });
      }
      return masked;
    });
  }, [form, selectedDocumentType]);

  useEffect(() => {
    if (!documentTypes?.length) return;
    const currentDocumentType = form.getValues("document_type_id");
    if (currentDocumentType) return;

    const cpfType = documentTypes.find((type) => type.name.toLowerCase() === "cpf");
    if (cpfType) {
      form.setValue("document_type_id", cpfType.id, { shouldDirty: false, shouldValidate: true });
    }
  }, [documentTypes, form]);

  // Default whatsapp flag on phones
  useEffect(() => {
    const currentPhones = form.getValues("phones");
    let updated = false;
    const updatedPhones = currentPhones.map((phone) => {
      if (phone.is_whatsapp === undefined) {
        updated = true;
        return { ...phone, is_whatsapp: true };
      }
      return phone;
    });
    if (updated) {
      form.setValue("phones", updatedPhones, { shouldDirty: false, shouldValidate: true });
    }
  }, [form]);

  // First email is always primary (CreateOwner behavior)
  useEffect(() => {
    // Enforce 1 email only (UI only supports one)
    const current = form.getValues("emails") ?? [];
    if (current.length === 0) {
      form.setValue("emails", [{ email: "", is_primary: true }], { shouldDirty: false, shouldValidate: false });
      return;
    }
    if (current.length > 1) {
      form.setValue("emails", [current[0]], { shouldDirty: true, shouldValidate: true });
      return;
    }
    if (current[0] && current[0].is_primary !== true) {
      form.setValue("emails.0.is_primary", true, { shouldDirty: false, shouldValidate: false });
    }
  }, [emails, form]);

  // Sync addressStates length with addresses length (CreateOwner behavior)
  useEffect(() => {
    if (!addresses || addresses.length === 0) {
      setAddressStates([]);
      return;
    }
    setAddressStates((prev) => {
      const next = addresses.map((_, index) => prev[index] ?? null);
      if (next.length === prev.length && next.every((value, index) => value === prev[index])) {
        return prev;
      }
      return next;
    });
  }, [addresses]);

  // Normalize primary phone/email/address flags like CreateOwner does
  useEffect(() => {
    if (!phones) return;
    let updated = false;
    const normalized = phones.map((phone, index) => {
      const shouldBePrimary = index === 0;
      if (phone.is_primary !== shouldBePrimary) {
        updated = true;
        return { ...phone, is_primary: shouldBePrimary };
      }
      return phone;
    });
    if (updated) {
      form.setValue("phones", normalized, { shouldDirty: false, shouldValidate: false });
    }
  }, [phones, form]);

  useEffect(() => {
    if (!addresses) return;
    let updated = false;
    const normalized = addresses.map((address, index) => {
      const shouldBePrimary = index === 0;
      if (address.is_primary !== shouldBePrimary) {
        updated = true;
        return { ...address, is_primary: shouldBePrimary };
      }
      return address;
    });
    if (updated) {
      form.setValue("addresses", normalized, { shouldDirty: false, shouldValidate: false });
    }
  }, [addresses, form]);

  const fetchCitiesForState = useCallback(
    async (stateId: string, index: number, cityNameToSelect?: string) => {
      if (!stateId) {
        setAddressCities((prev) => {
          const updated = { ...prev };
          delete updated[index];
          return updated;
        });
        form.setValue(`addresses.${index}.city_id`, "", { shouldDirty: true });
        return;
      }

      const { data, error } = await supabase
        .from("cities")
        .select("id, name, state_id")
        .eq("state_id", stateId)
        .order("name");

      if (error || !data) {
        return;
      }

      const typedData = data as City[];
      setAddressCities((prev) => ({ ...prev, [index]: typedData }));
      setCityNameCache((prev) => {
        const updated = { ...prev };
        typedData.forEach((city) => {
          updated[city.id] = city.name;
        });
        return updated;
      });

      if (cityNameToSelect) {
        const match = typedData.find((city) => city.name.toLowerCase() === cityNameToSelect.toLowerCase());
        if (match) {
          form.setValue(`addresses.${index}.city_id`, match.id, { shouldDirty: true });
        }
      }
    },
    [form],
  );

  const handleCepLookup = useCallback(
    async (index: number) => {
      const cepValue = (form.getValues(`addresses.${index}.zip_code`) as string) ?? "";
      const cepDigits = cepValue.replace(/\D/g, "");

      if (cepDigits.length !== 8) {
        form.setError(`addresses.${index}.zip_code`, {
          type: "manual",
          message: "Informe um CEP válido (8 dígitos)",
        });
        return;
      }

      // Always clear address fields before looking up CEP.
      form.setValue(`addresses.${index}.street`, "", { shouldDirty: true });
      form.setValue(`addresses.${index}.number`, "", { shouldDirty: true });
      form.setValue(`addresses.${index}.complement`, "", { shouldDirty: true });
      form.setValue(`addresses.${index}.neighborhood`, "", { shouldDirty: true });
      form.setValue(`addresses.${index}.city_id`, "", { shouldDirty: true });
      form.clearErrors(`addresses.${index}.city_id`);

      setAddressStates((prev) => {
        const updated = [...prev];
        updated[index] = null;
        return updated;
      });
      setAddressCities((prev) => {
        const updated = { ...prev };
        delete updated[index];
        return updated;
      });
      setCityDisplayValues((prev) => ({ ...prev, [index]: "" }));

      form.clearErrors(`addresses.${index}.zip_code`);

      try {
        setAddressCepLoading((prev) => ({ ...prev, [index]: true }));
        const data = await lookupCep(cepDigits);

        form.clearErrors(`addresses.${index}.zip_code`);

        if (data.street) {
          form.setValue(`addresses.${index}.street`, data.street, { shouldDirty: true });
        }
        if (data.neighborhood) {
          form.setValue(`addresses.${index}.neighborhood`, data.neighborhood, { shouldDirty: true });
        }
        if (data.complement) {
          form.setValue(`addresses.${index}.complement`, data.complement, { shouldDirty: true });
        }

        const stateMatch = states?.find((state) => state.code === data.state);
        if (stateMatch) {
          setAddressStates((prev) => {
            const updated = [...prev];
            updated[index] = stateMatch.id;
            return updated;
          });
          await fetchCitiesForState(stateMatch.id, index, data.city);
        }
      } catch (error) {
        console.error("Erro ao consultar CEP:", error);
        const code = error instanceof CepLookupError ? error.code : "LOOKUP_FAILED";
        form.setError(`addresses.${index}.zip_code`, {
          type: "manual",
          message: mapCepErrorToMessage(code),
        });
      } finally {
        setAddressCepLoading((prev) => ({ ...prev, [index]: false }));
      }
    },
    [fetchCitiesForState, form, states],
  );

  useEffect(() => {
    addressStates.forEach((stateId, index) => {
      if (stateId) {
        fetchCitiesForState(stateId, index).catch(() => {
          // Silencia erros do autocomplete de cidades
        });
      }
    });
  }, [addressStates, fetchCitiesForState]);

  // Birth date input sync
  useEffect(() => {
    const isoDate = form.getValues("birth_date");
    setBirthDateInput(formatISOToDisplay(isoDate));
  }, [form]);

  // Sync city display values with form state
  useEffect(() => {
    addresses?.forEach((_, index) => {
      const cityId = form.watch(`addresses.${index}.city_id`);
      const citiesForState = addressCities[index] ?? [];
      const selectedCity = citiesForState.find((city) => city.id === cityId);

      if (selectedCity && !cityDisplayValues[index]) {
        setCityDisplayValues((prev) => ({ ...prev, [index]: selectedCity.name }));
      } else if (cityId && cityNameCache[cityId] && !cityDisplayValues[index]) {
        setCityDisplayValues((prev) => ({ ...prev, [index]: cityNameCache[cityId] }));
      }
    });
  }, [addresses, addressCities, cityNameCache, cityDisplayValues, form]);

  // Preencher owner_id em todos os pet forms quando owner for carregado
  useEffect(() => {
    if (!ownerIdFromUrl) return;
    Object.values(petForms).forEach((petForm) => {
      if (petForm) {
        petForm.setValue("owner_id", ownerIdFromUrl, { shouldDirty: false });
      }
    });
  }, [ownerIdFromUrl, petForms]);

  // Preencher form com dados do owner se owner_id estiver na URL
  useEffect(() => {
    if (!existingOwner || !ownerIdFromUrl) return;

    // Preencher campos do owner
    form.reset({
      name: existingOwner.name || "",
      gender_id: existingOwner.gender_id || "",
      document_type_id: existingOwner.document_type_id || "",
      document: existingOwner.document || "",
      birth_date: existingOwner.birth_date || "",
      profession: existingOwner.profession || "",
      marketing_consent: existingOwner.marketing_consent ?? true,
      lead_source_id: existingOwner.lead_source_id || "",
      phones: existingOwner.phones && existingOwner.phones.length > 0
        ? existingOwner.phones.map((phone: { id?: string; phone_number?: string; is_whatsapp?: boolean; is_primary?: boolean }) => ({
          id: phone.id,
          phone_number: phone.phone_number || "",
          is_whatsapp: phone.is_whatsapp ?? false,
          is_primary: phone.is_primary ?? false,
        }))
        : [{ phone_number: "", is_whatsapp: true, is_primary: true }],
      emails: existingOwner.emails && existingOwner.emails.length > 0
        ? existingOwner.emails.map((email: { id?: string; email?: string; is_primary?: boolean }) => ({
          id: email.id,
          email: email.email || "",
          is_primary: email.is_primary ?? false,
        }))
        : [{ email: "", is_primary: true }],
      addresses: existingOwner.addresses && existingOwner.addresses.length > 0
        ? existingOwner.addresses.map((address: { id?: string; zip_code?: string; street?: string; number?: string; complement?: string; neighborhood?: string; city_id?: string; is_primary?: boolean; city?: { state_id?: string; name?: string } }, index: number) => {
          const city = address.city;
          if (city?.state_id) {
            setAddressStates((prev) => {
              const updated = [...prev];
              updated[index] = city.state_id;
              return updated;
            });
          }
          return {
            id: address.id,
            zip_code: address.zip_code || "",
            street: address.street || "",
            number: address.number || "",
            complement: address.complement || "",
            neighborhood: address.neighborhood || "",
            city_id: address.city_id || "",
            is_primary: address.is_primary ?? (index === 0),
          };
        })
        : [{
          zip_code: "",
          street: "",
          number: "",
          complement: "",
          neighborhood: "",
          city_id: "",
          is_primary: true,
        }],
    });

    // Atualizar document input
    if (existingOwner.document) {
      setDocumentInput(existingOwner.document);
    }

    // Atualizar birth date input
    if (existingOwner.birth_date) {
      setBirthDateInput(formatISOToDisplay(existingOwner.birth_date));
    }

    // Carregar cidades para os estados selecionados
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    existingOwner.addresses?.forEach((address: any, index: number) => {
      const city = address.city;
      if (city?.state_id) {
        fetchCitiesForState(city.state_id, index, city.name).catch(() => {
          // Silencia erros
        });
      }
    });
  }, [existingOwner, ownerIdFromUrl, form, fetchCitiesForState]);

  // Scroll and focus on newly created pet form
  useEffect(() => {
    if (!lastCreatedPetId) return;

    // Aguardar renderização do DOM
    setTimeout(() => {
      const petElement = document.querySelector(`[data-pet-id="${lastCreatedPetId}"]`);
      const nameInput = petElement?.querySelector('[data-pet-name-input]') as HTMLInputElement;

      if (petElement) {
        cancelScrollRef.current?.();
        const targetTop = Math.max(
          0,
          petElement.getBoundingClientRect().top + window.scrollY,
        );
        cancelScrollRef.current = startCancelableSmoothScroll(window, {
          top: targetTop,
        });

        // Focar no input após scroll
        setTimeout(() => {
          nameInput?.focus();
        }, 300);
      }

      setLastCreatedPetId(null); // Reset após uso
    }, 100);
  }, [lastCreatedPetId]);

  useEffect(() => {
    return () => {
      cancelScrollRef.current?.();
    };
  }, []);

  const goToStep2 = async () => {
    // Validate city fields manually before triggering form validation
    const addresses = form.getValues("addresses");

    const invalidCities = addresses?.map((address, index) => {
      const cityId = address.city_id;
      const displayValue = cityDisplayValues[index];

      // If there's text in the display but no city_id, set error
      if (displayValue && displayValue.trim() && !cityId) {
        return `addresses.${index}.city_id`;
      }
    }).filter(Boolean);

    const ok = await form.trigger();
    if (invalidCities.length) {
      invalidCities.forEach((cityKey) => {
        form.setError(cityKey as keyof OwnerInput, {
          type: "manual",
          message: t("wizard.validation.invalidCity"),
        });
      });
      toast({
        title: t("wizard.validation.genericTitle"),
        description: t("wizard.validation.genericDescription"),
        variant: "destructive",
      });
      scrollToTopOnError();
      return;
    }
    if (!ok) {
      toast({
        title: t("wizard.validation.genericTitle"),
        description: t("wizard.validation.genericDescription"),
        variant: "destructive",
      });
      scrollToTopOnError();
      return;
    }

    // Smooth scroll to top when proceeding to step 2
    cancelScrollRef.current?.();
    cancelScrollRef.current = startCancelableSmoothScroll(window, { top: 0 });
    setStep(2);
  };

  const goToStep1 = () => {
    setStep(1);
    // Limpar owner selecionado da busca ao voltar para o passo 1
    setSelectedOwnerIdFromSearch(null);
  };

  const isSaving = createOwner.isPending || createPet.isPending || uploadPhoto.isPending || uploadAttachment.isPending;

  // Check if there are unsaved changes in any form
  const hasUnsavedChanges = useMemo(() => {
    // Check owner form
    if (form.formState.isDirty) return true;

    // Check pet forms
    const hasDirtyPet = Object.values(petForms).some(
      (petForm) => petForm?.formState?.isDirty
    );

    return hasDirtyPet;
  }, [form.formState.isDirty, petForms]);

  const handleCancelClick = (e: React.MouseEvent) => {
    e.preventDefault();

    if (hasUnsavedChanges) {
      setIsExitDialogOpen(true);
    } else {
      smartBack();
    }
  };

  const handleConfirmExit = () => {
    setIsExitDialogOpen(false);
    smartBack();
  };

  const onFinalSubmit = async () => {
    // Se tiver owner_id na URL ou owner selecionado da busca, não validar nem criar owner
    if (!ownerIdFromUrl && !selectedOwnerIdFromSearch) {
      const ownerOk = await form.trigger();
      if (!ownerOk) {
        setStep(1);
        scrollToTopOnError();
        return;
      }
    }

    // Check if there are pet forms visible
    let petEntries: Array<{ id: string; form: UseFormReturn<PetInput> }> = [];

    if (petIds.length > 0) {
      // Get all pet forms (not just ones with names)
      const allPetForms = petIds
        .map((id) => ({ id, form: petForms[id] }))
        .filter((entry) => Boolean(entry.form));

      // Check if any form has data (name filled)
      const formsWithData = allPetForms.filter(entry => {
        const values = entry.form!.getValues();
        return Boolean(values?.name?.trim());
      });

      // If forms exist but none have names, require user to fill or remove them
      if (formsWithData.length === 0) {
        toast({
          title: t("wizard.validation.genericTitle"),
          description: t("wizard.validation.fillOnePatient"),
        });
        setStep(2);
        scrollToTopOnError();
        return;
      }

      // Validate all forms that have names
      let petsOk = true;
      for (const entry of formsWithData) {
        const ok = await entry.form!.trigger();
        if (!ok) petsOk = false;
      }
      if (!petsOk) {
        toast({
          title: t("wizard.validation.genericTitle"),
          description: t("wizard.validation.genericDescription"),
          variant: "destructive",
        });
        setStep(2);
        scrollToTopOnError();
        return;
      }

      // Store validated pets for saving
      petEntries = formsWithData;
    }

    // Preflight: prevent duplicate record_number before attempting any writes.
    // - Check duplicates within the current batch
    // - Check conflicts with existing pets (scoped by RLS/current clinic)
    if (petEntries.length > 0) {
      const entriesWithRecord = petEntries
        .map((entry) => {
          const values = entry.form?.getValues();
          const recordNumber = String(values?.record_number ?? "").trim();
          return recordNumber ? { entry, recordNumber } : null;
        })
        .filter((v): v is { entry: (typeof petEntries)[number]; recordNumber: string } => Boolean(v));

      const byRecordNumber = new Map<string, { entry: (typeof petEntries)[number]; recordNumber: string }[]>();
      for (const item of entriesWithRecord) {
        const list = byRecordNumber.get(item.recordNumber) ?? [];
        list.push(item);
        byRecordNumber.set(item.recordNumber, list);
      }

      const duplicated = Array.from(byRecordNumber.entries()).filter(([, list]) => list.length >= 2);
      if (duplicated.length > 0) {
        for (const [, list] of duplicated) {
          for (const { entry } of list) {
            entry.form?.setError("record_number", {
              type: "validate",
              message: t("wizard.validation.duplicateRecordNumber"),
            });
          }
        }

        duplicated[0]?.[1]?.[0]?.entry?.form?.setFocus("record_number");

        toast({
          title: t("wizard.errors.recordNumberConflictTitle"),
          description: t("createOwnerPage.recordNumberConflictBatch"),
          variant: "destructive",
        });
        scrollToTopOnError();
        return;
      }

      const recordNumbers = Array.from(byRecordNumber.keys());
      if (recordNumbers.length > 0) {
        const { data: existing, error } = await supabase
          .from("pets")
          .select("record_number, deleted_at")
          .in("record_number", recordNumbers)
          .is("deleted_at", null)
          .limit(recordNumbers.length);

        if (!error && existing && existing.length > 0) {
          const conflicts = new Set(
            existing.map((row) => String(row.record_number ?? "").trim()).filter((v) => v.length > 0)
          );

          if (conflicts.size > 0) {
            for (const item of entriesWithRecord) {
              if (!conflicts.has(item.recordNumber)) continue;
              item.entry.form?.setError("record_number", {
                type: "validate",
                message: t("wizard.validation.recordNumberInUse"),
              });
            }

            entriesWithRecord.find((item) => conflicts.has(item.recordNumber))?.entry?.form?.setFocus("record_number");

            toast({
              title: t("wizard.errors.recordNumberConflictTitle"),
              description: t("wizard.errors.recordNumberConflictDescription"),
              variant: "destructive",
            });
            scrollToTopOnError();
            return;
          }
        }
      }
    }

    let createdOwnerId: string | null = null;
    let ownerCreatedThisSubmit = false;
    let failedPetEntryId: string | null = null;

    const isRecordNumberConflict = (error: unknown) => {
      const maybe = error as { code?: string; message?: string; details?: string; hint?: string } | null;
      const code = maybe?.code ?? "";
      const msg = `${maybe?.message ?? ""} ${maybe?.details ?? ""} ${maybe?.hint ?? ""}`.toLowerCase();
      // This conflict typically comes from our trigger via `RAISE EXCEPTION`, which surfaces as `P0001`.
      // Still keep a fallback for unique index violations (`23505`) if schema changes in the future.
      const isUnique = code === "23505" || msg.includes("duplicate key");
      const isRaiseException = code === "P0001";
      const mentionsRecord =
        msg.includes("record_number") ||
        msg.includes("record number") ||
        msg.includes("número de ficha") ||
        msg.includes("numero de ficha") ||
        msg.includes("ficha");
      const triggerMessage =
        msg.includes("número de ficha já está em uso") ||
        msg.includes("numero de ficha ja esta em uso") ||
        msg.includes("já está em uso nesta clínica") ||
        msg.includes("ja esta em uso nesta clinica");

      if (isRaiseException) return mentionsRecord || triggerMessage;
      if (isUnique) return mentionsRecord;
      return false;
    };

    try {
      // Se tiver owner_id na URL, usar o owner existente
      // Se tiver owner selecionado da busca, usar o owner existente
      let owner;
      if (ownerIdFromUrl && existingOwner) {
        owner = existingOwner;
      } else if (selectedOwnerIdFromSearch && selectedOwnerFromSearch) {
        owner = selectedOwnerFromSearch;
      } else {
        // Sanitize addresses like CreateOwner does
        const data = form.getValues();

        // Sanitize contacts for DB constraints:
        // - owners_phones.phone_number is NOT NULL
        // - owners_emails.email is NOT NULL
        const sanitizedPhones = (data.phones ?? [])
          .map((phone) => ({
            ...phone,
            phone_number: phone.phone_number?.trim() ?? "",
          }))
          .filter((phone) => Boolean(phone.phone_number))
          .map((phone, index) => ({ ...phone, is_primary: index === 0 }));

        const sanitizedEmails = (data.emails ?? [])
          .map((email) => ({
            ...email,
            email: email.email?.trim() ?? "",
          }))
          .filter((email) => Boolean(email.email))
          .map((email, index) => ({ ...email, is_primary: index === 0 }));

        // If document is empty, don't persist document_type_id either (avoid type without a document).
        const sanitizedDocument = data.document?.trim() ?? "";
        const persistedDocument = sanitizedDocument
          ? removeDocumentMask(sanitizedDocument, selectedDocumentType)
          : "";
        const sanitizedDocumentTypeId = persistedDocument ? (data.document_type_id?.trim() ?? "") : "";
        const sanitizedAddresses = (data.addresses ?? [])
          .map((address) => ({
            ...address,
            zip_code: address.zip_code?.trim() ?? "",
            street: address.street?.trim() ?? "",
            number: address.number?.trim() ?? "",
            complement: address.complement?.trim() ?? "",
            neighborhood: address.neighborhood?.trim() ?? "",
            city_id: address.city_id?.trim() ?? "",
          }))
          .filter((address) =>
            Boolean(address.zip_code || address.street || address.number || address.complement || address.neighborhood || address.city_id),
          )
          .map((address, index) => ({ ...address, is_primary: index === 0 }));

        const ownerPayload: OwnerInput = {
          ...data,
          document: persistedDocument,
          document_type_id: sanitizedDocumentTypeId,
          phones: sanitizedPhones,
          emails: sanitizedEmails,
          addresses: sanitizedAddresses,
        };

        owner = await createOwner.mutateAsync(ownerPayload);
        createdOwnerId = owner.id;
        ownerCreatedThisSubmit = true;
      }

      let lastPetId: string | null = null;
      for (const entry of petEntries) {
        const petFormData = entry.form!.getValues();
        let pet;
        try {
          pet = await createPet.mutateAsync({
            ...petFormData,
            owner_id: owner.id,
            suppressToast: true,
          });
        } catch (err) {
          failedPetEntryId = entry.id;
          if (isRecordNumberConflict(err)) {
            entry.form!.setError("record_number", {
              type: "server",
              message: t("wizard.validation.recordNumberInUse"),
            });
            entry.form!.setFocus("record_number");
          }
          throw err;
        }

        if (pet?.id) {
          lastPetId = pet.id;
        }

        if (pet?.id) {
          await Promise.all([
            petFormData.photo
              ? uploadPhoto.mutateAsync({ petId: pet.id, file: petFormData.photo, suppressToast: true })
              : Promise.resolve(),
            petFormData.attachments && petFormData.attachments.length > 0
              ? Promise.all(
                  petFormData.attachments.map((attachment) =>
                    uploadAttachment.mutateAsync({
                      petId: pet.id,
                      file: attachment.file,
                      name: attachment.name,
                      type: attachment.type,
                      description: attachment.description,
                      documentDate: attachment.documentDate,
                      suppressToast: true,
                    }),
                  ),
                )
              : Promise.resolve(),
          ]);
        }
      }

      if (petEntries.length >= 1 && lastPetId) {
        // Não usar smartBack aqui: ele faz navigate(-1) quando há histórico e ignora o destino.
        navigate(`/patients/pets/${lastPetId}`, { replace: true });
      } else {
        // 0 animais: manter comportamento atual
        toast({
          title: t("wizard.success.ownerOnlyTitle"),
          description: t("wizard.success.ownerOnlyDescription"),
        });
        navigate("/patients", { replace: true });
      }
    } catch (err) {
      // Keep user on step 2 to fix record_number and retry.
      setStep(2);
      scrollToTopOnError();

      let ownerRollbackStatus: "none" | "rolled_back" | "queued" = "none";

      // If the owner was created in this submit attempt, rollback it to avoid duplicates.
      if (ownerCreatedThisSubmit && createdOwnerId) {
        const rollbackResult = await rollbackOwnerOrQueue(createdOwnerId, { queryClient, maxAttempts: 4 });
        ownerRollbackStatus = rollbackResult.status;

        if (rollbackResult.status === "queued") {
          console.error("Erro ao desfazer cadastro do responsável (em fila):", rollbackResult.error);
          toast({
            title: t("wizard.errors.rollbackQueuedTitle"),
            description: t("wizard.errors.rollbackQueuedDescription"),
            variant: "destructive",
          });
        }
      }

      if (isRecordNumberConflict(err)) {
        // If we couldn't map the error to a specific pet (rare), still guide the user.
        if (failedPetEntryId === null) {
          toast({
            title: t("wizard.errors.recordNumberConflictTitle"),
            description: t("wizard.errors.recordNumberConflictDescription"),
            variant: "destructive",
          });
          scrollToTopOnError();
          return;
        }

        toast({
            title: t("wizard.errors.recordNumberConflictTitle"),
            description:
              ownerRollbackStatus === "queued"
              ? t("wizard.errors.recordNumberConflictQueued")
              : t("wizard.errors.recordNumberConflictClean"),
          variant: "destructive",
        });
        scrollToTopOnError();
        return;
      }

      toast({
        title: t("wizard.errors.createPatientTitle"),
        description:
          ownerRollbackStatus === "queued"
            ? t("wizard.errors.createPatientQueued")
            : t("wizard.errors.createPatientClean"),
        variant: "destructive",
      });
      scrollToTopOnError();
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => smartBack()} aria-label={t("wizard.back")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-foreground">{t("wizard.title")}</h1>
        </div>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-6 border-b pb-3">
        <div className="flex items-center gap-2">
          <div
            className={cn(
              "min-h-8 min-w-8 rounded-full flex items-center justify-center",
              step === 1 ? "bg-background-gradient text-color-dark shadow-soft-search" : "bg-muted border-border text-muted-foreground",
            )}
          >
            <CustomIcons name="user" className={cn("!h-6 !w-6", step === 1 ? "text-color-dark" : "text-color-light")} />
          </div>
          <div>
            <p className={cn("text-sm", step === 1 ? "text-color-dark" : "text-color-light")}>
              <b>{t("wizard.steps.step1Search").split(":")[0]}:</b> {step1Mode === "search" ? t("wizard.cardTitles.searchOwner") : t("wizard.cardTitles.createOwner")}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div
            className={cn(
              "min-h-8 min-w-8 rounded-full flex items-center justify-center ",
              step === 2 ? "bg-background-gradient text-color-dark shadow-soft-search" : "border b-color-light text-color-light",
            )}
          >
            <CustomIcons name="paws" className={cn("!h-6 !w-6", step === 2 ? "text-color-dark" : "text-color-light")} />
          </div>
          <div>
            <p className={cn("text-sm", step === 2 ? "text-color-dark" : "text-color-light")}>
              <b>{t("wizard.steps.step2").split(":")[0]}:</b> {t("wizard.cardTitles.createPatient")}
            </p>
          </div>
        </div>
      </div>

      {isLoadingOwner && ownerIdFromUrl ? (
        <Card>
          <CardContent className="py-12">
            <div className="flex flex-col items-center justify-center gap-4">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
              <p className="text-sm text-muted-foreground">{t("wizard.loadingOwner")}</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Form {...form}>
          {/* We keep a single owner form; pets are managed separately like CreateOwner */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
            }}
            className="space-y-4 !mb-6"
          >
            <Card>
              <CardHeader className="flex flex-col items-start md:flex-row md:items-center md:justify-between gap-2">
                <CardTitle className="flex w-full flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex w-full flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      {step === 1 ? (step1Mode === "search" ? t("wizard.cardTitles.searchOwner") : t("wizard.cardTitles.createOwner")) : t("wizard.cardTitles.createPatient")}
                    </div>
                    {step === 1 && (
                      <div className="flex items-center">
                        <span className="text-sm font-normal text-muted-foreground">
                          {step1Mode === "create" ? t("wizard.toggle.alreadyRegistered") : t("wizard.toggle.notFound")}</span>
                        <Button
                          type="button"
                          variant="link"
                          className="ml-1 text-primary"
                          onClick={() => {
                            if (step1Mode === "search") {
                              enterCreateMode();
                              return;
                            }
                            setStep1Mode("search");
                            setSelectedOwnerIdFromSearch(null);
                          }}
                        >
                          {step1Mode === "create" ? t("wizard.toggle.search") : t("wizard.toggle.createNew")}
                        </Button>
                      </div>
                    )}
                  </div>
                </CardTitle>
                {step === 2 && (
                  <Button type="button" variant="outline" size="sm" onClick={addPetForm}>
                    <Plus className="h-4 w-4" />
                    {t("wizard.addPatient")}
                  </Button>
                )}
              </CardHeader>

              <CardContent className="space-y-8">
                {step === 1 ? (
                  step1Mode === "search" ? (
                    // Modo busca
                    <div className="space-y-6 min-h-[281px]">
                      <div className="space-y-4">
                        <SearchField
                          label={t("wizard.searchPlaceholder")}
                          clearLabel={t("common:clearSearch")}
                          placeholder={t("wizard.searchPlaceholder")}
                          value={ownerSearchInput}
                          onChange={(e) => setOwnerSearchInput(e.target.value)}
                          onClear={() => setOwnerSearchInput("")}
                        />

                        {(ownerSearchInput.trim().length >= 2 || searchedOwners.length > 0 || isLoadingOwnerSearch) && (
                          <Card className="mt-2 !shadow-none border overflow-hidden">
                            <CardContent className="p-0">
                              {isLoadingOwnerSearch ? (
                                <div className="py-8 text-center md:h-[280px] justify-center items-center flex flex-col">
                                  <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent mx-auto"></div>
                                  <p className="text-sm text-muted-foreground mt-2">{t("wizard.searching")}</p>
                                </div>
                              ) : searchedOwners.length === 0 ? (
                                <div className="py-8 text-center md:h-[280px] justify-center items-center flex flex-col">
                                  <p className="text-sm text-muted-foreground">{t("wizard.noOwnerFound")}</p>
                                  <p className="text-xs text-muted-foreground mt-1">{t("wizard.typeAnotherSearch")}</p>
                                </div>
                              ) : (
                                <div className="max-h-[400px] overflow-y-auto">
                                  {searchedOwners.map((owner, index) => {
                                    const email = primaryEmail(owner);
                                    const formattedDoc = formatDocument(owner.document);
                                    const isFirst = index === 0;
                                    const isLast = index === searchedOwners.length - 1;

                                    return (
                                      <button
                                        key={owner.id}
                                        type="button"
                                        onClick={() => handleSelectOwnerFromSearch(owner.id)}
                                        className={cn(
                                          "w-full text-left p-4 hover:bg-secondary transition-colors border-b last:border-b-0 focus:outline-none focus:bg-accent",
                                          isFirst && "rounded-t-lg",
                                          isLast && "rounded-b-lg"
                                        )}
                                      >
                                        <div className="flex items-start gap-3">
                                          <div className="flex-shrink-0 h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                                            <User className="h-5 w-5 text-muted-foreground" />
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <p className="font-medium text-foreground">{owner.name}</p>
                                            {email && (
                                              <p className="text-sm text-muted-foreground truncate mt-0.5">{email.email}</p>
                                            )}
                                            {formattedDoc && (
                                              <p className="text-xs text-muted-foreground mt-0.5">CPF: {formattedDoc}</p>
                                            )}
                                          </div>
                                        </div>
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        )}

                        {ownerSearchInput.trim().length < 2 && searchedOwners.length === 0 && !isLoadingOwnerSearch && (
                          <div className="text-center py-8 text-muted-foreground">
                            <Search className="h-12 w-12 mx-auto mb-3 opacity-50" />
                            <p className="text-sm">
                              {t("wizard.searchHint")}
                            </p>
                          </div>
                        )}
                      </div>

                      <div className="flex items-center justify-center pt-4 border-t">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            enterCreateMode();
                          }}
                          className="w-full sm:w-auto"
                        >
                          <User className="h-4 w-4" />
                          {t("wizard.createNewOwner")}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // Modo criar
                    <OwnerFormSection
                      form={form}
                      genders={genders}
                      documentTypes={documentTypes}
                      leadSources={leadSources}
                      states={states}
                      birthDateInput={birthDateInput}
                      setBirthDateInput={setBirthDateInput}
                      isBirthDateCalendarOpen={isBirthDateCalendarOpen}
                      setIsBirthDateCalendarOpen={setIsBirthDateCalendarOpen}
                      documentInput={documentInput}
                      setDocumentInput={setDocumentInput}
                      phones={phones ?? []}
                      addresses={addresses}
                      addressStates={addressStates}
                      setAddressStates={setAddressStates}
                      addressCities={addressCities}
                      addressCepLoading={addressCepLoading}
                      cityNameCache={cityNameCache}
                      setCityNameCache={setCityNameCache}
                      cityDisplayValues={cityDisplayValues}
                      setCityDisplayValues={setCityDisplayValues}
                      fetchCitiesForState={fetchCitiesForState}
                      handleAddAddress={handleAddAddress}
                      handleRemoveAddress={handleRemoveAddress}
                      handleCepLookup={handleCepLookup}
                    />
                  )
                ) : (
                  <div className="space-y-4">
                    {petIds.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <CustomIcons name="paws" className="!h-12 !w-12 mx-auto mb-3 opacity-50" />
                        <p className="font-medium">{t("wizard.emptyPatientsTitle")}</p>
                        <p className="text-sm mt-2">{t("wizard.emptyPatientsDescription")}</p>
                        <p className="text-sm mt-1">{t("wizard.emptyPatientsSecondary")}</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {petIds.map((petId, index) => (
                          <PetFormItem
                            key={petId}
                            id={petId}
                            index={index}
                            onRemove={() => removePetForm(petId)}
                            onFormReady={(petForm) => handleFormReady(petId, petForm)}
                            schema={petWizardSchema}
                            defaultRecordNumber={petDefaultRecordNumbers[petId] ?? null}
                            recordNumberUi={
                              isLoadingNextRecordNumber && (petDefaultRecordNumbers[petId] === null || petDefaultRecordNumbers[petId] === undefined)
                                ? { disabled: true, placeholder: t("petForm.placeholders.loading") }
                                : (isNextRecordNumberError || isNextRecordNumberInvalid)
                                  ? { disabled: false, placeholder: t("createPet.fields.recordNumber") }
                                  : { disabled: false, placeholder: t("petForm.placeholders.recordNumber") }
                            }
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Bottom action bar (outside card) */}
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <Button
                type="button"
                variant="outline"
                className="shadow-soft-search"
                onClick={step === 1 ? handleCancelClick : goToStep1}
              >
                <CustomIcons name="cancel-circle" className="!h-6 !w-6 !text-color-dark" />
                {step === 1 ? t("wizard.cancel") : t("wizard.back")}
              </Button>

              {step === 1 ? (
                step1Mode === "create" ? (
                  <Button
                    type="button"
                    className="w-full sm:w-auto"
                    onClick={goToStep2}
                  >
                    <CustomIcons name="check-circle" className="!h-6 !w-6 !text-primary-foreground" />
                    <span className="font-normal">
                      {t("wizard.proceedToPatient")}
                    </span>
                  </Button>
                ) : null
              ) : (
                <Button
                  type="button"
                  className="w-full sm:w-auto"
                  disabled={isSaving} onClick={onFinalSubmit}
                >
                  <CustomIcons name="check-circle" className="!h-6 !w-6 !text-primary-foreground" />
                  <span className="font-normal">
                    {isSaving ? "Salvando..." : "Salvar"}
                  </span>
                </Button>
              )}
            </div>
          </form>
        </Form>
      )}

      <ConfirmationDialog
        open={isExitDialogOpen}
        onOpenChange={setIsExitDialogOpen}
        title="Descartar alterações não salvas?"
        description="Você preencheu dados que ainda não foram salvos. Tem certeza que deseja sair sem salvar?"
        confirmText="Sim, descartar"
        cancelText="Continuar editando"
        onConfirm={handleConfirmExit}
        variant="destructive"
      />
    </div>
  );
}
